/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  InteractionManager,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography'
import { colors } from '../../../shared/theme/colors';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ModalScrollScreen } from '../../../shared/layout/ModalScrollScreen';
import { AuthScreenFrame } from '../../../shell/brand/AuthScreenFrame';
import { OperationalConfirmModal } from '../../../shared/modals/OperationalConfirmModal';
import { OnboardingWizardStepper } from './OnboardingWizardStepper';
import { protoProgress } from './protoProgress';
import { OnboardingWizardSteps } from './OnboardingWizardSteps';
import { OnboardingWelcomeScreen } from './OnboardingWelcomeScreen';
import { OnboardingSuccessScreen } from './OnboardingSuccessScreen';
import { OnboardingBrandRail } from './OnboardingBrandRail';
import { railCopyForWizardStep, wizardRailSteps } from './onboardingRailCopy';
import { useOnboardingLayout } from './useOnboardingLayout';
import { useOnboardingStateMachine, type OrganizationSetupMode } from './useOnboardingStateMachine';
import { trackOnboardingFunnel } from '../../../onboarding/application/onboardingFunnel';
import { hapticConfirm, hapticError } from '../../../shared/ui/haptics';

const MOBILE_STICKY_NAV_HEIGHT = 76;
const WIZARD_FINISH_LABEL = 'Crear mi negocio';
const WIZARD_FINISH_LABEL_ADDITIONAL = 'Crear negocio';

type WizardPhase = 'welcome' | 'wizard' | 'success';

type Props = {
  onComplete?: () => void | Promise<void>;
  setupMode?: OrganizationSetupMode;
  onAwaitingInvite?: () => void | Promise<void>;
  /** Antes de crear la org: el padre mantiene el wizard montado para la fase success. */
  onCreateStarted?: () => void;
};

export function OrganizationSetupWizard({
  onComplete,
  setupMode = 'initial',
  onAwaitingInvite,
  onCreateStarted,
}: Props) {
  const navigation = useNavigation<any>();
  const insets = useSafeAreaInsets();
  const layout = useOnboardingLayout();
  const sm = useOnboardingStateMachine({ setupMode });
  const {
    steps,
    stepIndex,
    currentStep,
    isLastStep,
    canProceed,
    businessName,
    setBusinessName,
    ownerName,
    setOwnerName,
    referralCode,
    setReferralCode,
    showReferralCodeField,
    pickerItems,
    selectedPickerKey,
    selectPickerItem,
    otherRubroLabel,
    setOtherRubroLabel,
    otherFeatureGroups,
    otherFeatureIds,
    toggleOtherFeatureId,
    loadingTypes,
    typesLoadError,
    retryPickerTypes,
    submitting,
    error,
    logoutConfirmVisible,
    setLogoutConfirmVisible,
    handleBack: smHandleBack,
    handleConfirmLogout,
    handleNext: smHandleNext,
    gastronomyStockMode,
    setGastronomyStockMode,
  } = sm;

  const [phase, setPhase] = useState<WizardPhase>(setupMode === 'additional' ? 'wizard' : 'welcome');
  const funnelStartedRef = useRef(false);
  const [exitConfirmVisible, setExitConfirmVisible] = useState(false);
  const mobileScrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const desktopScrollRef = useRef<KeyboardAwareScrollViewRef>(null);
  const mobileScrollContentRef = useRef<View>(null);
  const [stickyNavHeight, setStickyNavHeight] = useState(MOBILE_STICKY_NAV_HEIGHT);
  const businessNameInputRef = useRef<TextInput>(null);
  const otherRubroLabelInputRef = useRef<TextInput>(null);

  const useStickyWizardNav = !layout.useSplitLayout;
  const canStackGoBack = navigation.canGoBack?.() ?? false;
  /** Proto: siempre «Atrás» en el wizard (paso 1 → bienvenida). */
  const backLabel = 'Atrás';
  /** Proto desktop: "← Atrás" ghost link; a11y label queda "Atrás". */
  const backDisplay = layout.useSplitLayout ? `← ${backLabel}` : backLabel;

  // Funnel (#1362): una sola vez por montaje, al entrar al wizard.
  useEffect(() => {
    if (phase !== 'wizard' || funnelStartedRef.current) return;
    funnelStartedRef.current = true;
    trackOnboardingFunnel('onboarding_wizard_view', { setup_mode: setupMode });
  }, [phase, setupMode]);

  /** Al cambiar de paso, volver arriba para leer título / instrucción. */
  useEffect(() => {
    if (phase !== 'wizard') return;
    const handle = InteractionManager.runAfterInteractions(() => {
      mobileScrollRef.current?.scrollTo({ y: 0, animated: false });
      desktopScrollRef.current?.scrollTo({ y: 0, animated: false });
    });
    return () => handle.cancel();
  }, [currentStep.id, phase]);

  const handleBack = () => {
    if (stepIndex === 0 && setupMode === 'initial') {
      setPhase('welcome');
      return;
    }
    smHandleBack(canStackGoBack, canStackGoBack ? navigation.goBack : () => {});
  };
  const handleNext = async () => {
    if (!canProceed) {
      hapticError();
      void smHandleNext(undefined, undefined, navigation);
      return;
    }
    if (isLastStep) {
      if (setupMode === 'initial') {
        onCreateStarted?.();
      }
      const outcome = await smHandleNext(
        setupMode === 'initial' ? undefined : onComplete,
        setupMode === 'initial' ? undefined : canStackGoBack ? navigation.goBack : undefined,
        navigation,
      );
      // #290: transición explícita a `success` desde el handler de submit en
      // lugar del useEffect sobre `submitting` (que podía correr en un render
      // intermedio y generar races). El state machine devuelve `{ created }`
      // cuando el alta inicial se completa sin error.
      if (setupMode === 'initial' && outcome) {
        setPhase('success');
      }
    } else {
      hapticConfirm();
      trackOnboardingFunnel('onboarding_step_completed', {
        step: currentStep.id,
        setup_mode: setupMode,
      });
      void smHandleNext(undefined, undefined, navigation);
    }
  };

  const enteredViaOtherRubro = sm.enteredViaOtherRubro;
  const railCopy = railCopyForWizardStep(currentStep.id, {
    otherRubro: enteredViaOtherRubro && currentStep.id === 'other-rubro',
  });
  const railSteps = wizardRailSteps(currentStep.id, {
    otherRubro: enteredViaOtherRubro && currentStep.id === 'other-rubro',
  });

  const { current: protoCurrent, total: protoTotal } = protoProgress(currentStep.id);

  const wizardScrollBody = (
    <>
      {/* Mobile: progress 1/3 + barra. Desktop split: kicker "Paso N de N" (proto). */}
      {!layout.useSplitLayout ? (
        <OnboardingWizardStepper
          steps={steps}
          currentIndex={stepIndex}
          stepId={currentStep.id}
          variant="horizontal"
        />
      ) : null}
      {layout.useSplitLayout ? (
        <Text style={styles.stepEyebrow}>{`Paso ${protoCurrent} de ${protoTotal}`}</Text>
      ) : null}

      <View style={styles.stepHeaderBlock}>
        <Text style={styles.stepTitle}>{currentStep.title}</Text>
      </View>
      {currentStep.subtitle ? (
        <Text style={styles.stepSubtitle}>{currentStep.subtitle}</Text>
      ) : null}

      <OnboardingWizardSteps
        currentStepId={currentStep.id}
        pickerItems={pickerItems}
        selectedPickerKey={selectedPickerKey}
        onSelectPickerItem={selectPickerItem}
        layout={layout}
        loadingTypes={loadingTypes}
        typesLoadError={typesLoadError}
        onRetryPickerTypes={retryPickerTypes}
        submitting={submitting}
        otherFeatureGroups={otherFeatureGroups}
        otherFeatureIds={otherFeatureIds}
        onToggleOtherFeatureId={toggleOtherFeatureId}
        otherRubroLabel={otherRubroLabel}
        onChangeOtherRubroLabel={setOtherRubroLabel}
        otherRubroLabelInputRef={otherRubroLabelInputRef}
        businessName={businessName}
        onChangeBusinessName={setBusinessName}
        ownerName={ownerName}
        onChangeOwnerName={setOwnerName}
        referralCode={referralCode}
        onChangeReferralCode={setReferralCode}
        showReferralCodeField={showReferralCodeField}
        businessNameInputRef={businessNameInputRef}
        canProceed={canProceed}
        onNext={() => void handleNext()}
        gastronomyStockMode={gastronomyStockMode}
        onSelectGastronomyStockMode={setGastronomyStockMode}
      />

      {error ? <Text style={styles.error}>{error}</Text> : null}
    </>
  );

  const wizardNav = (
    <View
      style={[
        styles.navRow,
        useStickyWizardNav ? styles.navRowStickyMobile : null,
        layout.useSplitLayout ? styles.navRowDesktop : null,
      ]}
    >
      <TouchableOpacity
        style={[styles.secondaryBtn, layout.useSplitLayout && styles.secondaryBtnDesktop]}
        onPress={handleBack}
        disabled={submitting}
        accessibilityRole="button"
        accessibilityLabel={backLabel}
        testID="onboarding.back"
      >
        <Text
          style={[
            styles.secondaryBtnLabel,
            layout.useSplitLayout && styles.secondaryBtnLabelDesktop,
          ]}
          numberOfLines={1}
        >
          {backDisplay}
        </Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[
          styles.primaryBtn,
          layout.useSplitLayout && styles.primaryBtnDesktop,
          (!canProceed || submitting) && styles.primaryBtnDisabled,
        ]}
        onPress={() => void handleNext()}
        disabled={!canProceed || submitting}
        accessibilityRole="button"
        accessibilityLabel={isLastStep ? 'Crear mi negocio' : 'Siguiente'}
        testID="onboarding.next"
      >
        {submitting ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <View style={styles.primaryBtnContent}>
            <Text style={styles.primaryLabel} numberOfLines={1}>
              {isLastStep
                ? setupMode === 'additional'
                  ? WIZARD_FINISH_LABEL_ADDITIONAL
                  : WIZARD_FINISH_LABEL
                : 'Siguiente'}
            </Text>
            <Ionicons
              name={isLastStep ? 'checkmark-outline' : 'arrow-forward-outline'}
              size={18}
              color="#FFFFFF"
            />
          </View>
        )}
      </TouchableOpacity>
    </View>
  );

  const hasWizardData =
    stepIndex > 0 ||
    businessName.trim().length > 0 ||
    otherFeatureIds.length > 0 ||
    otherRubroLabel.trim().length > 0;

  const handleClose = () => {
    if (canStackGoBack && !hasWizardData) {
      navigation.goBack();
    } else if (canStackGoBack) {
      setExitConfirmVisible(true);
    } else {
      setLogoutConfirmVisible(true);
    }
  };

  /** Proto mobile: sin “Armá tu local” arriba. Solo X si hay que salir (org adicional). */
  const wizardHeader =
    layout.useSplitLayout || setupMode === 'initial' ? null : (
      <View style={styles.topbar}>
        <View style={{ flex: 1 }} />
        <TouchableOpacity
          style={styles.closeBtn}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        >
          <Ionicons name="close-outline" size={22} color={ticketColors.ink2} />
        </TouchableOpacity>
      </View>
    );

  /** Proto v2: el paso del picker lleva card ancha (700 / 3 cols); el resto 520. */
  const desktopFormMaxWidth = currentStep.id === 'business-type' ? 700 : 520;

  const desktopSplit = (
    <View style={styles.desktopSplitRoot}>
      <OnboardingBrandRail
        title={railCopy.title}
        subtitle={railCopy.subtitle}
        steps={railSteps}
        footer={railCopy.footer}
      />
      <View style={styles.desktopFormCol}>
        <ModalScrollScreen
          ref={desktopScrollRef}
          variant="auth"
          contentMaxWidth={desktopFormMaxWidth}
          transparentBackground
          style={styles.desktopFormScroll}
          contentContainerStyle={styles.desktopFormScrollInner}
          keyboardShouldPersistTaps="handled"
          keyboardBottomPadding={72}
          showsVerticalScrollIndicator={false}
        >
          <View style={[styles.desktopFormInner, { maxWidth: desktopFormMaxWidth }]}>
            {wizardScrollBody}
          </View>
        </ModalScrollScreen>
        <View style={styles.desktopNavDock}>
          <View style={[styles.desktopNavDockInner, { maxWidth: desktopFormMaxWidth }]}>
            {wizardNav}
          </View>
        </View>
      </View>
    </View>
  );

  const mobileWizardPanel = (
    <View
      style={[
        styles.mobileWizardFrame,
        layout.isWide ? styles.mobileWizardFrameCard : styles.mobileWizardFrameFull,
        {
          maxWidth: layout.cardMaxWidth,
          padding: layout.cardPadding,
          ...(!layout.isWide ? { paddingTop: layout.cardPadding + Math.max(insets.top, 0) } : null),
        },
      ]}
    >
      <ModalScrollScreen
        ref={mobileScrollRef}
        contentRef={mobileScrollContentRef}
        variant="default"
        transparentBackground
        style={styles.mobileScroll}
        contentContainerStyle={styles.scrollMobileStickyInner}
        keyboardBottomPadding={stickyNavHeight + spacing.lg}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
      >
        <View style={[styles.mobileWizardScrollBody, { paddingBottom: stickyNavHeight + spacing.xl }]}>
          {wizardHeader}
          {wizardScrollBody}
        </View>
      </ModalScrollScreen>
      <View
        onLayout={(event) => {
          const nextHeight = event.nativeEvent.layout.height;
          if (nextHeight > 0 && Math.abs(nextHeight - stickyNavHeight) > 1) {
            setStickyNavHeight(nextHeight);
          }
        }}
        style={[styles.stickyNavInset, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}
      >
        {wizardNav}
      </View>
    </View>
  );

  if (phase === 'welcome') {
    return (
      <OnboardingWelcomeScreen
        onStart={() => setPhase('wizard')}
        onAwaitingInvite={setupMode === 'initial' ? onAwaitingInvite : undefined}
        stepCount={steps.length}
      />
    );
  }

  if (phase === 'success') {
    return (
      <OnboardingSuccessScreen
        businessName={businessName}
        showSupportFollowUp={sm.otherRubroSupportNote}
        onGoSell={() => {
          if (canStackGoBack) navigation.goBack();
          InteractionManager.runAfterInteractions(() => {
            void onComplete?.();
          });
        }}
      />
    );
  }

  return (
    <AuthScreenFrame
      showWatermark={false}
      backgroundColor={layout.useSplitLayout ? '#FFFFFF' : undefined}
    >
      <View style={styles.root}>
        {layout.useSplitLayout ? (
          desktopSplit
        ) : (
          <View
            style={[styles.mobileWizardShell, layout.isWide ? { padding: layout.contentPadding } : null]}
          >
            {mobileWizardPanel}
          </View>
        )}
      </View>
      <OperationalConfirmModal
        visible={logoutConfirmVisible}
        title="Cerrar sesión"
        message="Vas a salir de la app."
        confirmLabel="Cerrar sesión"
        iconName="log-out-outline"
        onClose={() => setLogoutConfirmVisible(false)}
        onConfirm={handleConfirmLogout}
      />
      <OperationalConfirmModal
        visible={exitConfirmVisible}
        title="¿Salir de la configuración?"
        message="Vas a perder lo que ingresaste. Podés volver a configurar tu negocio cuando quieras."
        confirmLabel="Salir igual"
        destructive
        iconName="close-outline"
        onClose={() => setExitConfirmVisible(false)}
        onConfirm={() => {
          setExitConfirmVisible(false);
          if (canStackGoBack) navigation.goBack();
        }}
      />
    </AuthScreenFrame>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  scrollMobileStickyInner: { flexGrow: 1 },
  mobileWizardShell: { flex: 1, justifyContent: 'flex-start' },
  mobileWizardFrame: {
    flex: 1,
    width: '100%',
    alignSelf: 'center',
    backgroundColor: '#F4F7F6',
    overflow: 'hidden',
  },
  mobileWizardFrameCard: {
    borderWidth: 1,
    borderColor: 'rgba(15, 23, 42, 0.1)',
    borderRadius: 28,
  },
  mobileWizardFrameFull: { borderWidth: 0, borderRadius: 0 },
  mobileWizardScrollBody: { gap: spacing.md },
  mobileScroll: { flex: 1 },
  stickyNavInset: {
    paddingTop: 12,
    backgroundColor: '#F4F7F6',
    borderTopWidth: 0,
  },
  desktopSplitRoot: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: ticketColors.paper,
  },
  desktopFormCol: {
    flex: 1,
    minWidth: 360,
    backgroundColor: ticketColors.paper,
    flexDirection: 'column',
    minHeight: 0,
  },
  desktopFormScroll: {
    flex: 1,
    minHeight: 0,
  },
  desktopFormScrollInner: {
    flexGrow: 1,
    paddingTop: 28,
    paddingBottom: 28,
    paddingHorizontal: 32,
  },
  desktopFormInner: {
    width: '100%',
    alignSelf: 'center',
    gap: spacing.md,
  },
  desktopNavDock: {
    flexShrink: 0,
    paddingHorizontal: 32,
    paddingTop: 12,
    paddingBottom: 20,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
  },
  desktopNavDockInner: {
    width: '100%',
  },
  topbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topbarTitle: { ...typography.titleLg, color: ticketColors.ink },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepHeaderBlock: { gap: spacing.xs },
  stepEyebrow: {
    ...typography.caption,
    textTransform: 'uppercase',
    letterSpacing: 0.9,
    color: ticketColors.brandPrimary,
    fontWeight: '700',
    fontSize: 11,
  },
  stepTitle: { ...typography.titleLg, color: ticketColors.ink },
  stepSubtitle: { ...typography.body, color: ticketColors.ink3, marginBottom: spacing.sm },
  error: { ...typography.caption, color: ticketColors.bad },
  navRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: spacing.sm,
    marginTop: 0,
  },
  /** Proto .card-footer: back a la izquierda, CTA compacto a la derecha. */
  navRowDesktop: { justifyContent: 'space-between' },
  secondaryBtnDesktop: {
    minHeight: 44,
  },
  secondaryBtnLabelDesktop: {
    color: ticketColors.ink2,
    fontSize: 13,
  },
  /** Proto .cta: CTA compacto a la derecha, no full-width (eso es mobile).
   *  OJO: no usar `flex: 0` — en web compila con flex-basis 0 y colapsa el
   *  ancho (texto cortado). Se anula solo el grow; el ancho sale del contenido. */
  primaryBtnDesktop: {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'auto',
    alignSelf: 'flex-end',
    minHeight: 44,
    borderRadius: 12,
    paddingHorizontal: 24,
  },
  navRowStickyMobile: { marginTop: 0 },
  secondaryBtn: {
    flexShrink: 0,
    minHeight: 52,
    minWidth: 72,
    borderWidth: 0,
    borderRadius: 18,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  secondaryBtnLabel: {
    fontSize: 14,
    color: ticketColors.ink3,
    fontWeight: '600',
    textAlign: 'center',
  },
  primaryBtn: {
    flex: 1,
    minHeight: 52,
    backgroundColor: colors.accent,
    borderRadius: 18,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  primaryBtnContent: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  primaryBtnDisabled: { opacity: 0.55 },
  primaryLabel: {
    ...typography.body,
    fontSize: 15,
    color: '#FFFFFF',
    fontWeight: '700',
    textAlign: 'center',
  },
});
